import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Lock, Brain, BarChart3, Shield, Zap, TrendingUp, Database, Globe, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from '../data/mock';
import BrandName from '../components/BrandName';
import DashboardMockup from '../components/DashboardMockup';

const iconMap = {
  Lock: Lock,
  Brain: Brain,
  BarChart3: BarChart3,
  Shield: Shield,
  Zap: Zap,
  TrendingUp: TrendingUp,
  Database: Database,
  Globe: Globe,
};

const Platform = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6" style={{ background: 'linear-gradient(180deg, #A9C9D9 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-6 py-2 rounded-full mb-8" style={{ background: 'rgba(244, 215, 64, 0.2)' }}>
              <span className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>PLATFORM OVERVIEW</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-8" style={{ color: '#1A1A1A' }}>
              Enterprise ESG Intelligence
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto mb-12">
              The most advanced platform for blockchain-verified, AI-powered sustainability management
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-20"
            style={{ color: '#1A1A1A' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Core Capabilities
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-12">
            {mockData.features.map((feature, index) => {
              const Icon = iconMap[feature.icon] || Shield;
              return (
                <motion.div
                  key={index}
                  className="card-premium p-10 rounded-3xl bg-white border-2 border-gray-100"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                >
                  <div className="p-4 rounded-2xl inline-block mb-6" style={{ background: 'rgba(124, 184, 214, 0.1)' }}>
                    <Icon size={40} style={{ color: '#7CB8D6' }} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A1A1A' }}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {feature.description}
                  </p>
                  <div className="space-y-3">
                    {feature.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center text-gray-700">
                        <CheckCircle size={18} className="mr-3" style={{ color: '#F4D740' }} />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Dashboard Preview */}
      <section className="py-32 px-6" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#1A1A1A' }}>
              Real-Time ESG Dashboard
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Monitor all sustainability metrics in one unified, intelligent interface
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <DashboardMockup />
            <p className="text-center text-sm text-gray-400 mt-6">Illustrative product preview with sample data</p>
          </motion.div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-20"
            style={{ color: '#1A1A1A' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Built on Cutting-Edge Technology
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-16">
            <TechCard
              title="Blockchain Infrastructure"
              description="Immutable ledger technology ensures every data point is cryptographically secured and permanently verifiable."
              features={[
                'Ethereum-based smart contracts',
                'Multi-chain compatibility',
                'Zero-knowledge proofs for privacy',
                'Sub-second transaction finality',
              ]}
            />
            <TechCard
              title="AI & Machine Learning"
              description="Advanced algorithms transform raw data into predictive insights and actionable intelligence."
              features={[
                'Predictive carbon modeling',
                'Anomaly detection systems',
                'Natural language processing',
                'Continuous learning optimization',
              ]}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6" style={{ background: 'linear-gradient(135deg, #7CB8D6 0%, #A9C9D9 100%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white">
              Experience the Platform
            </h2>
            <p className="text-xl text-white/90 mb-12">
              See how <BrandName /> can transform your ESG strategy
            </p>
            <Link to="/contact">
              <motion.button
                className="btn-premium px-12 py-6 rounded-full text-xl font-semibold"
                style={{ background: '#F4D740', color: '#1A1A1A' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Request a Demo
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const TechCard = ({ title, description, features }) => {
  return (
    <motion.div
      className="p-10 rounded-3xl bg-white border-2 border-gray-100"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A1A1A' }}>
        {title}
      </h3>
      <p className="text-gray-600 mb-6 leading-relaxed">
        {description}
      </p>
      <ul className="space-y-3">
        {features.map((feature, i) => (
          <li key={i} className="flex items-center text-gray-700">
            <CheckCircle size={18} className="mr-3" style={{ color: '#7CB8D6' }} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

export default Platform;